import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import {
  PlayArrow,
  PlaylistPlay,
  FitnessCenter,
  Schedule,
  LocalFireDepartment,
  CheckCircle,
  BedtimeOutlined
} from '@mui/icons-material';
import PremiumCard from 'components/shared/surfaces/PremiumCard';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import { MY_WEEKLY_ROUTINE, MY_PLAY_WORKOUT_SUMMARY } from 'api/graphql/play';
import { muscleLabel, dayLabel, todayIso, primaryMuscleOf } from 'components/play/constants';
import { buildWorkoutPlan, estimateWorkout } from 'components/play/workoutEstimate';
import playWorkoutSlice, { selectWorkoutActive } from 'store/modules/playWorkout';
import routes from 'common/routes';

// Tile compacta para un stat (mismo lenguaje que los counters del dashboard).
function StatTile({ icon, label, value, hint = null }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        p: 1.5,
        borderRadius: '14px',
        border: '1px solid',
        borderColor: 'hairline.main',
        backgroundColor: 'surface.overlay'
      }}>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75, color: 'gold.main' }}>
        {icon}
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.02em' }} noWrap>
          {label}
        </Typography>
      </Stack>
      <Typography variant="h6" fontWeight={800} sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }} noWrap>
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
          {hint}
        </Typography>
      )}
    </Box>
  );
}

StatTile.propTypes = {
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  hint: PropTypes.string
};

/**
 * OTRIX Play — card "Rutina de hoy" del dashboard. Muestra el músculo real del día, stats
 * (reales + estimados) y los CTA Iniciar rutina (motor de ejecución) / Ver mi rutina.
 * Monocromo + dorado como acento (sustituye el naranja del mockup del cliente).
 */
function PlayTodayRoutineCard({ compact = false }) {
  const { t } = useTranslation(['play']);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const membershipStatus = useSelector((state) => state.auth.user?.membership_status);
  const isActive = membershipStatus === 'ACTIVE';
  const workoutActive = useSelector(selectWorkoutActive);

  const { data } = useQuery(MY_WEEKLY_ROUTINE, { fetchPolicy: 'cache-and-network' });
  const { data: summaryData } = useQuery(MY_PLAY_WORKOUT_SUMMARY, { fetchPolicy: 'cache-and-network' });

  const iso = todayIso();
  const items = useMemo(() => data?.myWeeklyRoutine?.items || [], [data]);
  const todayItems = useMemo(() => items.filter((i) => i.day_of_week === iso), [items, iso]);

  // Nº de días de entrenamiento en la semana (denominador de "Sesión N/M").
  const trainingDays = useMemo(() => {
    const days = new Set(items.map((i) => i.day_of_week));
    return days.size || 1;
  }, [items]);

  const primaryMuscle = useMemo(() => primaryMuscleOf(todayItems), [todayItems]);

  // Semana del mesociclo (1..4). Null = rutina armada a mano, sin progresión.
  const progressionWeek = data?.myWeeklyRoutine?.progression_week || null;

  const estimate = useMemo(() => estimateWorkout(todayItems, progressionWeek || 1), [todayItems, progressionWeek]);

  const completedThisWeek = summaryData?.myPlayWorkoutSummary?.completed_this_week || 0;
  const sessionNumber = Math.min(completedThisWeek + 1, trainingDays);

  const handleStart = () => {
    if (!workoutActive) {
      dispatch(
        playWorkoutSlice.actions.start({
          dayOfWeek: iso,
          muscleFocus: primaryMuscle,
          exercises: buildWorkoutPlan(todayItems, progressionWeek || 1),
          now: Date.now(),
          progressionWeek
        })
      );
    }
    navigate(routes.playWorkout.path);
  };

  const title = primaryMuscle
    ? t('todayCard.titleMuscle', { ns: 'play', muscle: muscleLabel(t, primaryMuscle) })
    : t('todayCard.titleGeneric', { ns: 'play' });

  // Estado día de descanso.
  if (todayItems.length === 0) {
    return (
      <PremiumCard>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 56,
                height: 56,
                // Sin esto el flex del Stack lo aplasta en mobile y la luna sale ovalada.
                flex: 'none',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid',
                borderColor: 'hairline.main',
                color: 'gold.main',
                backgroundColor: 'surface.overlay'
              }}>
              <BedtimeOutlined />
            </Box>
            <Stack spacing={0.25}>
              <Typography variant="overline" sx={{ color: 'gold.main', letterSpacing: '0.16em', fontWeight: 700 }}>
                {dayLabel(t, iso)}
              </Typography>
              <Typography variant="h6" fontWeight={800}>
                {t('todayCard.restTitle', { ns: 'play' })}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('todayCard.restBody', { ns: 'play' })}
              </Typography>
            </Stack>
          </Stack>
          <Button variant="text" onClick={() => navigate(routes.playRoutine.path)} sx={{ color: 'gold.main', fontWeight: 700, alignSelf: { xs: 'flex-start', sm: 'center' } }}>
            {t('todayCard.viewRoutine', { ns: 'play' })}
          </Button>
        </Stack>
      </PremiumCard>
    );
  }

  return (
    <PremiumCard>
      <Stack spacing={2.5}>
        {/* Encabezado */}
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              position: 'relative',
              width: 56,
              height: 56,
              flex: 'none',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'gold.main',
              backgroundColor: 'gold.soft',
              border: '1px solid',
              borderColor: 'gold.main',
              // Pulso dorado sutil y continuo (solo transform/opacity, sin glow protagonista).
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: -4,
                borderRadius: '50%',
                border: '1px solid',
                borderColor: 'gold.main',
                opacity: 0,
                animation: 'otxTodayPulse 2.4s cubic-bezier(0.32, 0.72, 0, 1) infinite'
              },
              '@keyframes otxTodayPulse': {
                '0%': { transform: 'scale(1)', opacity: 0.5 },
                '100%': { transform: 'scale(1.35)', opacity: 0 }
              }
            }}>
            <FitnessCenter />
          </Box>
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography variant="overline" sx={{ color: 'gold.main', letterSpacing: '0.16em', fontWeight: 700 }}>
              {t('todayCard.eyebrow', { ns: 'play' })} · {dayLabel(t, iso)}
            </Typography>
            <Typography variant="h5" fontWeight={800} letterSpacing="-0.02em" noWrap>
              {title}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('todayCard.exercisesToday', { ns: 'play', count: estimate.exerciseCount, sets: estimate.totalSets })}
            </Typography>
            {/* Semana del mesociclo. Monocromo: el acento dorado ya lo lleva el eyebrow. */}
            {progressionWeek && (
              <Chip
                size="small"
                label={progressionWeek === 4 ? t('mesocycle.deload', { ns: 'play' }) : t('mesocycle.week', { ns: 'play', week: progressionWeek })}
                sx={{ alignSelf: 'flex-start', mt: 0.5, height: 22, bgcolor: 'surface.overlay', color: 'text.secondary', fontWeight: 700 }}
              />
            )}
          </Stack>
        </Stack>

        {/* Stats reales + estimados */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <StatTile
            icon={<PlaylistPlay sx={{ fontSize: 18 }} />}
            label={t('todayCard.statSession', { ns: 'play' })}
            value={`${sessionNumber}/${trainingDays}`}
          />
          <StatTile
            icon={<CheckCircle sx={{ fontSize: 18 }} />}
            label={t('todayCard.statStatus', { ns: 'play' })}
            value={isActive ? t('todayCard.statActive', { ns: 'play' }) : t('todayCard.statInactive', { ns: 'play' })}
          />
          <StatTile
            icon={<Schedule sx={{ fontSize: 18 }} />}
            label={t('todayCard.statDuration', { ns: 'play' })}
            value={t('todayCard.minutes', { ns: 'play', count: estimate.durationMin })}
          />
          <StatTile
            icon={<LocalFireDepartment sx={{ fontSize: 18 }} />}
            label={t('todayCard.statCalories', { ns: 'play' })}
            value={t('todayCard.kcal', { ns: 'play', count: estimate.calories })}
          />
        </Stack>

        {/* CTA. En `compact` (columna angosta del home de Play) van SIEMPRE apilados: en fila,
            "Continuar entrenamiento" partía en dos líneas y estiraba la card. */}
        <Stack direction={compact ? 'column' : { xs: 'column', sm: 'row' }} spacing={1.5}>
          <Button
            startIcon={<PlayArrow />}
            onClick={handleStart}
            sx={{ ...premiumButtonSx, flex: 1 }}>
            {workoutActive ? t('todayCard.resume', { ns: 'play' }) : t('todayCard.start', { ns: 'play' })}
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => navigate(routes.playRoutine.path)}
            sx={{ flex: 1, borderColor: 'hairline.strong', fontWeight: 700, textTransform: 'none', py: 1.4, borderRadius: 2.5 }}>
            {t('todayCard.viewRoutine', { ns: 'play' })}
          </Button>
        </Stack>
        {workoutActive && (
          <Chip
            size="small"
            label={t('miniBar.inProgress', { ns: 'play' })}
            sx={{ alignSelf: 'flex-start', bgcolor: 'gold.soft', color: 'gold.main', fontWeight: 700 }}
          />
        )}
      </Stack>
    </PremiumCard>
  );
}

PlayTodayRoutineCard.propTypes = {
  /** true = la card vive en una columna angosta (home de Play) → CTAs apilados. */
  compact: PropTypes.bool
};

export default PlayTodayRoutineCard;
